'use client'

import React, { useEffect, useId, useRef, useState } from 'react'

import { useOverlayStack } from './useOverlayStack'
import ReactDOM from 'react-dom'

import {
  Box,
  ChakraProvider,
  SystemStyleObject,
  chakra,
} from '@chakra-ui/react'

import { system } from '../../theme/system'
import { ColorScheme } from '../../types'
import { UtilityButton } from '../Button'
import { ArrowLeftIcon } from '../Icon'
import {
  DRAWER_TRANSITION_MS,
  bodyStyles,
  footerStyles,
  headerActionsStyles,
  headerStyles,
  overlayStyles,
  panelStyles,
  titleStyles,
} from './Drawer.styles'

export type DrawerProps = {
  isOpen: boolean
  onClose: () => void
  /** Title rendered in the default header row. Optional when a custom `header`
   * is supplied. */
  title?: string
  /** Scrollable body (`overflow-y: auto`). */
  children: React.ReactNode
  /** Fully custom header, rendered in place of the default title row (back
   * button + title + headerActions). The node owns its own layout/padding.
   * Pair with `ariaLabel` for the dialog's accessible name. */
  header?: React.ReactNode
  /** Accessible name for the dialog when a custom `header` is used (the default
   * header labels the dialog via its title instead). */
  ariaLabel?: string
  /** When provided, renders a back button left of the title. */
  onBack?: () => void
  /** Optional right-aligned header slot (e.g. "View Filters", "Save Filter"). */
  headerActions?: React.ReactNode
  /** Optional sticky footer (e.g. Clear All / Apply). */
  footer?: React.ReactNode
  colorScheme?: ColorScheme
  width?: string
  /** Override the overlay z-index. Defaults to 1000, below nested Modals. */
  zIndex?: number
  /** Style overrides applied to the panel container (e.g. a different surface). */
  panelStyleOverrides?: SystemStyleObject
  /** Style overrides applied to the body wrapper element. */
  bodyStyleOverrides?: SystemStyleObject
}

export const Drawer = ({
  isOpen,
  onClose,
  title,
  children,
  header,
  ariaLabel,
  onBack,
  headerActions,
  footer,
  colorScheme = 'dark',
  width = '448px',
  zIndex,
  panelStyleOverrides,
  bodyStyleOverrides,
}: DrawerProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const titleId = useId()

  // Exit animation: stay mounted while sliding out. `isVisible` drives the
  // transform/opacity transition; unmount happens on transitionend (with a
  // timeout fallback for environments that never fire it, e.g. jsdom).
  const [isMounted, setIsMounted] = useState(isOpen)
  const [isVisible, setIsVisible] = useState(false)

  // Mount during render (not in an effect) so the panel exists by the time
  // useOverlayStack's focus effect runs - otherwise wrapperRef is still null
  // on the open that follows a close.
  if (isOpen && !isMounted) setIsMounted(true)

  useOverlayStack({ isOpen, onClose, wrapperRef })

  useEffect(() => {
    if (isOpen) {
      // Flip to the open state on the next frame so the panel transitions
      // from its just-mounted closed position instead of appearing in place.
      // rAF can be throttled indefinitely in occluded/background windows, so
      // a timeout fallback guarantees the drawer still becomes visible there
      // (the slide is skipped, which is fine when nothing is painting).
      const frame = requestAnimationFrame(() => setIsVisible(true))
      const fallback = setTimeout(() => setIsVisible(true), 100)
      return () => {
        cancelAnimationFrame(frame)
        clearTimeout(fallback)
      }
    }
    setIsVisible(false)
    const timer = setTimeout(
      () => setIsMounted(false),
      DRAWER_TRANSITION_MS + 100
    )
    return () => clearTimeout(timer)
  }, [isOpen])

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (
      !isOpen &&
      e.target === e.currentTarget &&
      e.propertyName === 'transform'
    ) {
      setIsMounted(false)
    }
  }

  // A Chakra v1 Modal/Drawer underneath this one (the consuming app still has
  // plenty) mounts react-remove-scroll, which listens for wheel/touchmove on
  // `document` and preventDefaults every event whose target sits outside its own
  // content. This drawer portals to document.body, so it is always "outside" and
  // its body stops scrolling while that lock is up (ENG-35869). Chakra v1 exposes
  // neither `shards` nor `noIsolation`, so the escape has to be here: stop the
  // events at the overlay, before they reach the document listener. Same class of
  // fix as `data-no-focus-lock` below.
  useEffect(() => {
    const overlay = overlayRef.current
    if (!overlay) return

    const stopPropagation = (e: Event) => e.stopPropagation()

    overlay.addEventListener('wheel', stopPropagation, { passive: false })
    overlay.addEventListener('touchmove', stopPropagation, { passive: false })
    return () => {
      overlay.removeEventListener('wheel', stopPropagation)
      overlay.removeEventListener('touchmove', stopPropagation)
    }
  }, [isMounted])

  if (!isMounted || typeof document === 'undefined') return null

  return ReactDOM.createPortal(
    <ChakraProvider value={system}>
      {/* data-no-focus-lock exempts this portal from any active react-focus-lock
          trap (e.g. a Chakra v1 Drawer with trapFocus) - without it, the trap
          reclaims focus from the drawer's inputs on every click. */}
      <Box
        data-no-focus-lock
        data-state={isVisible ? 'open' : 'closed'}
        {...overlayStyles(colorScheme, zIndex, isVisible)}
        onClick={onClose}
        ref={overlayRef}
      >
        <Box
          aria-label={header ? ariaLabel : undefined}
          aria-labelledby={header ? undefined : titleId}
          aria-modal='true'
          onClick={(e) => e.stopPropagation()}
          onTransitionEnd={handleTransitionEnd}
          ref={wrapperRef}
          role='dialog'
          tabIndex={-1}
          {...panelStyles(colorScheme, width, isVisible)}
          {...panelStyleOverrides}
        >
          {/* Header: a fully custom node when provided, else the default
              back-button + title + actions row. */}
          {header ?? (
            <Box {...headerStyles(colorScheme)}>
              {onBack && (
                <UtilityButton
                  colorScheme={colorScheme}
                  icon={<ArrowLeftIcon height={18} width={18} />}
                  label='Back'
                  onClick={onBack}
                />
              )}
              <chakra.h2 id={titleId} m={0} {...titleStyles(colorScheme)}>
                {title}
              </chakra.h2>
              {headerActions && (
                <Box {...headerActionsStyles}>{headerActions}</Box>
              )}
            </Box>
          )}

          {/* Body */}
          <Box {...bodyStyles(colorScheme)} {...bodyStyleOverrides}>
            {children}
          </Box>

          {/* Footer */}
          {footer && <Box {...footerStyles(colorScheme)}>{footer}</Box>}
        </Box>
      </Box>
    </ChakraProvider>,
    document.body
  )
}
